import { useEffect, useRef } from 'react';
import { useSchedulerContext } from '../context/SchedulerContext';
import { SchedulerEvent } from '../types/scheduler.types';
import { pickClosestEventToToday } from '../utils/claimSearchHelpers';

export interface ClaimSearchScrollerProps {
  /** The claim number to search for and scroll to */
  claimNumber: string | null | undefined;
  /** List of scheduler events to search within */
  events: SchedulerEvent[];
  /** Whether data is still loading */
  isLoading: boolean;
}

/**
 * Helper component that automatically scrolls the scheduler to show a task
 * when filtering by claim number.
 *
 * This component:
 * 1. Waits for events to load
 * 2. Finds the event matching the claim number
 * 3. Scrolls horizontally to the event's date
 * 4. Scrolls vertically to the resource row
 *
 * Must be rendered inside SchedulerProvider.
 */
export function ClaimSearchScroller({
  claimNumber,
  events,
  isLoading,
}: ClaimSearchScrollerProps) {
  const { schedulerRef } = useSchedulerContext();
  const scrolledToClaimRef = useRef<string | null>(null);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const verticalScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Clear scroll timers on unmount
  useEffect(() => {
    return () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      if (verticalScrollTimerRef.current)
        clearTimeout(verticalScrollTimerRef.current);
    };
  }, []);

  useEffect(() => {
    // Skip if no claim number, still loading, or already scrolled for this claim
    if (
      !claimNumber ||
      isLoading ||
      scrolledToClaimRef.current === claimNumber
    ) {
      return;
    }

    // Skip if no events
    if (!events || events.length === 0) {
      return;
    }

    const scheduler = schedulerRef.current?.instance;
    if (!scheduler) {
      return;
    }

    const matchedEvent = pickClosestEventToToday(events, claimNumber);

    if (matchedEvent && matchedEvent.startDate) {
      // Mark that we've scrolled for this claim
      scrolledToClaimRef.current = claimNumber;

      // Capture startDate before setTimeout to satisfy TypeScript narrowing
      const eventStartDate = matchedEvent.startDate;
      const eventResourceId = matchedEvent.resourceId;

      // Use setTimeout to ensure the scheduler has rendered the events
      // Delay needed for Bryntum to complete rendering after data load
      const SCROLL_DELAY_MS = 300;
      const VERTICAL_SCROLL_DELAY_MS = 200;

      scrollTimerRef.current = setTimeout(() => {
        scrollTimerRef.current = null;
        try {
          const taskStartDate = new Date(eventStartDate);

          // Scroll horizontally to the task's date
          (
            scheduler as { scrollToDate: (date: Date, options: object) => void }
          ).scrollToDate(taskStartDate, {
            block: 'center',
            animate: true,
          });

          // Also scroll vertically to the resource row if we have resourceId
          if (eventResourceId) {
            const resourceRecord =
              scheduler.resourceStore?.getById(eventResourceId);
            if (resourceRecord) {
              verticalScrollTimerRef.current = setTimeout(() => {
                verticalScrollTimerRef.current = null;
                (
                  scheduler as {
                    scrollResourceIntoView: (
                      resource: unknown,
                      options: object
                    ) => void;
                  }
                ).scrollResourceIntoView(resourceRecord, {
                  animate: true,
                });
              }, VERTICAL_SCROLL_DELAY_MS);
            }
          }
        } catch (error) {
          // Log error in development for debugging scroll issues
          if (process.env['NODE_ENV'] === 'development') {
            // eslint-disable-next-line no-console
            console.warn(
              '[ClaimSearchScroller] Failed to scroll to event:',
              error
            );
          }
        }
      }, SCROLL_DELAY_MS);
    }
  }, [claimNumber, events, isLoading, schedulerRef]);

  // Reset when claim is cleared
  useEffect(() => {
    if (!claimNumber) {
      scrolledToClaimRef.current = null;
    }
  }, [claimNumber]);

  return null;
}
